import React, { useState, useEffect } from 'react';
import { Modal, TextField } from '@ann/web-components';
import { observer } from 'mobx-react';
import { useStore } from '../../../../../hooks';
import { useToast } from '../../../../../components/toast';
import { ErrorMessage } from './RenameFolderModal.styled';

const RenameFolderModal = ({
    size = "medium",
    modalWidth = '500px',
    isOpen,
    onClose,
    onSave,
    folderUuid,
    initialName = '',
    disabled = false
}) => {
    const { foldersStore } = useStore();
    const toast = useToast();
    const [folderName, setFolderName] = useState(initialName);
    const [touched, setTouched] = useState(false);
    
    const isFieldEmpty = !folderName.trim();
    const showError = touched && isFieldEmpty;

    // Update name when initialName changes
    useEffect(() => {
        if (isOpen) {
            setFolderName(initialName);
            setTouched(false);
        }
    }, [isOpen, initialName]);

    const handleSave = async () => {
        try {
            const renameData = {
                folder_uuid: folderUuid,
                name: folderName.trim() || 'Untitled Folder'
            };

            const result = await foldersStore.renameFolder(renameData);
            
            // Endpoint always responds (HTTP 200), check error observable
            if (result) {
                // Check if there's an error (statusCode !== 200)
                if (foldersStore.isErrorRenameFolder) {
                    // Error case (400, 404, 409, 500)
                    toast.error({ 
                        message: foldersStore.errorRenameFolderMessage, 
                        canClose: true 
                    });
                    
                    if (onSave) {
                        onSave({ 
                            success: false, 
                            errorMessage: foldersStore.errorRenameFolderMessage 
                        });
                    }
                    
                    // Don't close modal - let user correct the name
                    
                } else {
                    // Success case (statusCode 200)
                    toast.success({ 
                        message: "Folder renamed successfully.", 
                        canClose: true 
                    });
                    
                    if (onSave) {
                        onSave({
                            success: true,
                            folderUuid,
                            newName: folderName.trim(),
                            updatedFolder: result && result.folderUuid ? result : { folderUuid, name: folderName.trim() }
                        });
                    }
                    
                    handleClose();
                }
                
            } else {
                // Result is null - should never happen
            }
            
        } catch (error) {
            // Network error (real errors)
            toast.error({ 
                message: foldersStore.errorRenameFolderMessage || 'Network error. Please check your connection.', 
                canClose: true 
            });
            
            if (onSave) {
                onSave({ 
                    success: false, 
                    errorMessage: foldersStore.errorRenameFolderMessage 
                });
            }
        }
    };

    const handleClose = () => {
        setFolderName(initialName);
        setTouched(false);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} width={modalWidth} size={size}>
            <Modal.Header>
                <Modal.Header.Title>
                    Rename folder
                </Modal.Header.Title>
            </Modal.Header>
            <Modal.Body>
                <TextField 
                    label="Folder Name"
                    size="small" 
                    placeholder="Untitled Folder"
                    value={folderName}
                    onChange={(value) => {
                        setFolderName(value);
                        if (!touched && value.length > 0) {
                            setTouched(true);
                        }
                    }}
                    autoFocus
                    required={true}
                />
                {showError && (
                    <ErrorMessage>Folder name is required</ErrorMessage>
                )}
            </Modal.Body>
            <Modal.Actions align="end">
                <Modal.Button
                    label="Cancel"
                    onClick={handleClose}
                    type="secondary"
                />
                <Modal.Button
                    label={foldersStore.isRenamingFolder ? "Saving..." : "Save"}
                    onClick={handleSave}
                    disabled={disabled || foldersStore.isRenamingFolder || isFieldEmpty}
                />
            </Modal.Actions>
        </Modal>
    );
};

export default observer(RenameFolderModal);

